export namespace IStyleQuiz {
  export namespace ISelectedApparelOptions {
    /**
     * Return an instance of ISelectedApparelOptions with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ISelectedApparelOptions>
    ): ISelectedApparelOptions =>
      ({
        options: [],
        ...(partial ?? {}),
      }) as ISelectedApparelOptions;
  }

  /**
   * Contains an array of options for each question during apparel onboarding.
   * Deprecated
   */
  export interface ISelectedApparelOptions {
    options: Array<string>;
  }

  /**
   * Contains all apparel ratio for "How much of your inventory is apparel or footwear" question
   * during apparel style quiz v2.
   * Deprecated
   */
  export enum ApparelRatio {
    APPAREL_RATIO_UNKNOWN = 'APPAREL_RATIO_UNKNOWN',
    ALL = 'ALL',
    MOST = 'MOST',
    SOME = 'SOME',
    NONE = 'NONE',
    UNANSWERED = 'UNANSWERED',
  }

  export enum StyleQuizStoreType {
    STYLE_QUIZ_STORE_TYPE_UNKNOWN = 'STYLE_QUIZ_STORE_TYPE_UNKNOWN',
    OTHER = 'OTHER',
    GIFT = 'GIFT',
    CLOTHING = 'CLOTHING',
    HOME_DECOR = 'HOME_DECOR',
    SPA_OR_SALON = 'SPA_OR_SALON',
    GENERAL = 'GENERAL',
    FLORIST_OR_GARDEN = 'FLORIST_OR_GARDEN',
    KIDS_OR_TOY = 'KIDS_OR_TOY',
    CAFE_OR_RESTAURANT = 'CAFE_OR_RESTAURANT',
    FITNESS_OR_YOGA = 'FITNESS_OR_YOGA',
    FURNITURE = 'FURNITURE',
    PET = 'PET',
    BOOK = 'BOOK',
    GROCERY_OR_LIQUOR = 'GROCERY_OR_LIQUOR',
    STATIONERY = 'STATIONERY',
    KIDS_AND_BABY = 'KIDS_AND_BABY',
  }

  export namespace IStoreTypeName {
    /**
     * Return an instance of IStoreTypeName with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IStoreTypeName>): IStoreTypeName =>
      ({
        ...(partial ?? {}),
      }) as IStoreTypeName;
  }

  export interface IStoreTypeName {
    /**
     * display to the client, this string is translated
     */
    display_name: string;
    /**
     * we save this value to our table, never translate this
     */
    canonical_name: string;
    store_type: keyof typeof IStyleQuiz.StyleQuizStoreType;
  }

  /**
   * Return an instance of IStyleQuiz with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IStyleQuiz>): IStyleQuiz =>
    ({
      categories: [],
      category_path_identifiers: [],
      styles: [],
      brand_tokens: [],
      apparel_answers: {},
      secondary_store_types: [],
      ...(partial ?? {}),
    }) as IStyleQuiz;
}

export interface IStyleQuiz {
  /**
   * @protobufOption (.faire.common.tokenType) = STYLE_QUIZ
   */
  token: string | undefined;
  /**
   * @protobufOption (.faire.common.tokenType) = RETAILER
   */
  retailer_token: string | undefined;
  /**
   * Examples, Coffee Shop, Bookstore, etc. Always in English, regardless of the locale of the retailer.
   */
  store_type: string | undefined;
  /**
   * Store type from the list of data curated store type
   */
  gpt_curated_store_type: string | undefined;
  /**
   * Indicate that storeType and gptCuratedStoreType are generated by chatGPT.
   */
  is_a_i_generated_store_type: boolean | undefined;
  /**
   * The categories of products that this store carries. Deprecated, use categoryPathIdentifiers instead.
   * @deprecated
   */
  categories: Array<string>;
  category_path_identifiers: Array<string>;
  styles: Array<string>;
  /**
   * Brand tokens that the retailer picked as representative of their style.
   * @protobufOption (.faire.common.tokenType) = BRAND
   */
  brand_tokens: Array<string>;
  /**
   * In the UI this is displayed as 1-5 stars.
   */
  eco_friendly_rating: number | undefined;
  hand_made_rating: number | undefined;
  made_in_usa_rating: number | undefined;
  made_in_canada_rating: number | undefined;
  not_sold_on_amazon_rating: number | undefined;
  local_rating: number | undefined;
  socially_conscious_rating: number | undefined;
  /**
   * In the UI this is displayed as $, $$, $$$, $$$$.
   * This is mapped as $ = 1, $$ = 2, $$$ = 3, $$$$ = 4.
   */
  price_point: number | undefined;
  updated_at: number | undefined;
  /**
   * Whether this style quiz was started.
   */
  started: boolean | undefined;
  /**
   * Whether this style quiz was completed.
   * @deprecated
   */
  completed: boolean | undefined;
  /**
   * Whether this style quiz is being filled out as part of the store profile experiment.
   */
  from_store_profile: boolean | undefined;
  /**
   * Should only be used when the storeType is Other.
   */
  other_store_type: string | undefined;
  /**
   * QuestionType string to SelectedApparelOptions map.
   * @deprecated
   */
  apparel_answers: Partial<Record<string, IStyleQuiz.ISelectedApparelOptions>>;
  /**
   * A boolean to determine if the request is sent from additional categories page during apparel onboarding.
   */
  from_additional_categories_page: boolean | undefined;
  /**
   * The apparel ratio for "How much of your inventory is apparel or footwear"
   * question during apparel style quiz v2.
   * @deprecated
   */
  apparel_ratio: keyof typeof IStyleQuiz.ApparelRatio | undefined;
  secondary_store_types: Array<string>;
  untranslated_other_store_type: string | undefined;
  original_locale: string | undefined;
}
